import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, Check, X } from 'lucide-react';

import { listFeedback, updateFeedbackStatus } from '../../services/api/adminFeedback';
import { FeedbackFilters } from '../../components/feedback/FeedbackFilters';
import type {
  AdminFeedbackType,
  FeedbackSeverity,
  FeedbackStatus,
  FeedbackListItem,
  ListFeedbackParams,
} from '../../types/feedback';
import { FEEDBACK_STATUSES } from '../../types/feedback';
import {
  FEEDBACK_STATUS_COLORS,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_TYPE_LABELS,
} from '../../utils/feedback-labels';
import { SEVERITY_COLORS, SEVERITY_LABELS } from '../../utils/severity-labels';

const STORAGE_KEY = 'feedback-filters';

const saveFilters = (params: URLSearchParams) => {
  const filters: Record<string, string> = {};
  const status = params.get('status');
  const type = params.get('type');
  const severity = params.get('severity');
  if (status) filters.status = status;
  if (type) filters.type = type;
  if (severity) filters.severity = severity;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
};

export const FeedbackQueuePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStatusFilter = searchParams.get('status');
  const statusFilter = FEEDBACK_STATUSES.includes(rawStatusFilter as FeedbackStatus)
    ? (rawStatusFilter as FeedbackStatus)
    : undefined;
  const typeFilter = (searchParams.get('type') as AdminFeedbackType) || undefined;
  const severityFilter = (searchParams.get('severity') as FeedbackSeverity) || undefined;

  useLayoutEffect(() => {
    const hasAnyParam =
      searchParams.has('status') || searchParams.has('type') || searchParams.has('severity');
    if (hasAnyParam) {
      saveFilters(searchParams);
      return;
    }

    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Record<string, string>;
        const params = new URLSearchParams();
        if (parsed.status) params.set('status', parsed.status);
        if (parsed.type) params.set('type', parsed.type);
        if (parsed.severity) params.set('severity', parsed.severity);
        if ([...params.keys()].length > 0) {
          setSearchParams(params, { replace: true });
          return;
        }
      } catch {}
    }

    setSearchParams({ status: 'new' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [allItems, setAllItems] = useState<FeedbackListItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);

  const queryParams: ListFeedbackParams = {
    status: statusFilter,
    type: typeFilter,
    severity: severityFilter,
    limit: 20,
    cursor,
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'admin-feedback',
      statusFilter ?? null,
      typeFilter ?? null,
      severityFilter ?? null,
      cursor ?? null,
    ],
    queryFn: () => listFeedback(queryParams),
  });

  const queryClient = useQueryClient();

  const quickStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) =>
      updateFeedbackStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-feedback'] });
    },
  });

  useEffect(() => {
    if (data) {
      if (!cursor) {
        setAllItems(data.items);
      } else {
        setAllItems((prev) => [...prev, ...data.items]);
      }
      setHasMore(data.nextCursor != null);
    }
  }, [data, cursor]);

  const handleFilterChange = useCallback(
    (key: string, value: string | undefined) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      params.delete('cursor');
      saveFilters(params);
      setSearchParams(params);
      setCursor(undefined);
      setAllItems([]);
      setHasMore(true);
    },
    [searchParams, setSearchParams],
  );

  const handleLoadMore = useCallback(() => {
    if (data?.nextCursor) {
      setCursor(data.nextCursor);
    }
  }, [data?.nextCursor]);

  const clearFilters = useCallback(() => {
    const params = new URLSearchParams({ status: 'new' });
    saveFilters(params);
    setSearchParams(params);
    setCursor(undefined);
    setAllItems([]);
    setHasMore(true);
  }, [setSearchParams]);

  const hasActiveFilters =
    !!(statusFilter && statusFilter !== 'new') || !!typeFilter || !!severityFilter;

  return (
    <section className="flex h-full flex-col gap-6">
      <header>
        <h2 className="text-3xl font-display text-brand-blue">Feedback Queue</h2>
        <p className="text-sm text-text-grey mt-1">Granska och hantera admin-feedback.</p>
      </header>

      <FeedbackFilters
        statusFilter={statusFilter}
        typeFilter={typeFilter}
        severityFilter={severityFilter}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-brand-blue" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>Kunde inte ladda feedback: {(error as Error).message}</span>
        </div>
      ) : allItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-text-grey">
          <p className="text-lg font-medium text-brand-blue">Ingen feedback ännu</p>
          <p className="mt-2 text-sm">
            Feedback från playground visas här när admin har skapat den.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-black/10">
          <table className="w-full text-sm">
            <thead className="bg-brand-beige-tint-1 text-left text-xs font-semibold uppercase tracking-wide text-text-grey">
              <tr>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Typ</th>
                <th className="px-4 py-3">Klassificering</th>
                <th className="px-4 py-3">Användarens fråga</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Skapad</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {allItems.map((item) => (
                <tr
                  key={item.id}
                  className="group transition-colors hover:bg-brand-beige-tint-1/50 cursor-pointer"
                  onClick={() => navigate(`/feedback/${item.id}`)}
                >
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-sm border px-2 py-0.5 text-xs font-medium ${SEVERITY_COLORS[item.severity]}`}
                    >
                      {SEVERITY_LABELS[item.severity]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-brand-blue">{FEEDBACK_TYPE_LABELS[item.type]}</td>
                  <td className="px-4 py-3">
                    {item.classification ? (
                      <span className="rounded-sm bg-purple-50 px-2 py-0.5 text-xs text-purple-700">
                        {item.classification}
                      </span>
                    ) : (
                      <span className="text-xs text-text-grey">—</span>
                    )}
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-brand-blue">
                    {item.userMessage}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-sm border px-2 py-0.5 text-xs font-medium ${FEEDBACK_STATUS_COLORS[item.status]}`}
                    >
                      {FEEDBACK_STATUS_LABELS[item.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-text-grey">
                    {new Date(item.createdAt).toLocaleDateString('sv-SE', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/feedback/${item.id}`);
                        }}
                        className="text-xs font-medium text-brand-blue underline hover:text-brand"
                      >
                        Öppna
                      </button>
                      <div className="ml-2 hidden items-center gap-1 group-hover:flex">
                        {item.status !== 'resolved' ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              quickStatusMutation.mutate({ id: item.id, status: 'resolved' });
                            }}
                            disabled={quickStatusMutation.isPending}
                            className="rounded-sm border border-green-300 bg-green-50 p-1 text-green-700 transition-colors hover:bg-green-100 disabled:opacity-50"
                            title="Markera som löst"
                          >
                            <Check className="h-3 w-3" />
                          </button>
                        ) : null}
                        {item.status !== 'rejected' ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              quickStatusMutation.mutate({ id: item.id, status: 'rejected' });
                            }}
                            disabled={quickStatusMutation.isPending}
                            className="rounded-sm border border-slate-300 bg-slate-50 p-1 text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50"
                            title="Markera som avvisad"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hasMore ? (
            <div className="flex justify-center border-t border-black/5 px-4 py-4">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={isFetching}
                className="rounded-sm border border-brand-blue bg-white px-4 py-2 text-sm text-brand-blue transition-colors hover:bg-brand-blue hover:text-white disabled:opacity-50"
              >
                {isFetching ? 'Laddar...' : 'Ladda fler'}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
};
